import React from 'react';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import AirplanemodeActiveIcon from '@mui/icons-material/AirplanemodeActive';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { usePapaParse } from 'react-papaparse';
import L from "leaflet";
import { getDistance } from "geolib";
import he from 'he';
import { matchSorter } from 'match-sorter';
import pointInPolygon from 'point-in-polygon';

import CustomAreaPopup from './Components/CustomArea.js';
import Storage from '../Storage.js';
import log from '../util/logger.js';
import { apiHits, hideAirport, wrapNb } from "../util/utility.js";
import { cleanJobs } from '../services/fseJobs.js';

import aircrafts from "../data/aircraft.json";

const storage = new Storage();

function NetworkError(code) {
  this.code = code;
}
function ParsingError(text) {
  const found = text.match(/<Error>(.*)<\/Error>/i);
  if (found !== null) {
    this.msg = found[1];
  }
}
function updateAlert(error) {
  if (error instanceof NetworkError) {
    if (error.code === 503) {
      alert('Could not get data: FSEconomy is in maintenance, try again later.');
    }
    else {
      alert('Could not get data: FSEconomy is down, try again later');
    }
  }
  else if (error instanceof ParsingError && error.msg) {
    alert('Could not get data: '+error.msg);
  }
  else {
    alert('Could not get data: FSEconomy is down or in maintenance. Try again later.');
  }
}

// Generate country list
function getAreas(icaodata, icaos) {
  let a = new Set();
  for (var i = icaos.length - 1; i >= 0; i--) {
    const country = icaodata[icaos[i]].country;
    if (country === 'United States') {
      a.add('US - '+icaodata[icaos[i]].state)
    }
    a.add(country);
  }
  return ["Custom area", "Around planes", ...[...a].sort()];
}

// Get the list of all airports within the selected countries and custom area
function getIcaoList(countries, latlngs, layers, icaodata, icaos, settings) {
  let center = null;
  let polygon = null;
  // If custom area, compute the area center to later wrap lng coordinates
  if (countries.includes('Custom area')) {
    const p = L.polygon(latlngs);
    center = p.getBounds().getCenter();
    polygon = p.getLatLngs()[0].map(elm => [elm.lat, elm.lng]);
  }
  let i = [];
  for (const icao of icaos) {
    // If custom area, check if icao is inside the polygon
    if (center && pointInPolygon([icaodata[icao].lat, wrapNb(icaodata[icao].lon, center.lng)], polygon)) {
      i.push(icao);
    }
    else {
      // For the United States, we can use states instead
      if (icaodata[icao].country === 'United States' && countries.includes('US - '+icaodata[icao].state)) {
        i.push(icao);
      }
      else if (countries.includes(icaodata[icao].country)) {
        i.push(icao);
      }
      else {
        for (const layer of layers) {
          if (layer.icaos.includes(icao)) {
            i.push(icao);
            break;
          }
        }
      }
    }
  }
  // If around planes, get ICAOs
  if (countries.includes('Around planes')) {
    let set = new Set();
    // Get plane locations
    const planes = storage.get('planes', {});
    for (const model of Object.keys(planes)) {
      for (const icao of Object.keys(planes[model])) {
        set.add(icao);
      }
    }
    set = [...set];
    // If we need to search around these airports
    if (settings.update.jobsPlanes !== "strict") {
      // If there is too many location, restrict to lower rent planes
      if (set.length > settings.update.jobsPlanesMax) {
        let list = {};
        for (const model of Object.keys(planes)) {
          for (const icao of Object.keys(planes[model])) {
            for (const plane of planes[model][icao]) {
              if (!plane.wet && !plane.dry) {
                list[icao] = 0;
              }
              if (!plane.dry) { continue; }
              if (list[icao]) {
                list[icao] = Math.min(set[icao], plane.dry);
              }
              else {
                list[icao] = plane.dry;
              }
            }
          }
        }
        list = Object.entries(list).sort((a, b) => a[1] - b[1]);
        set = list.slice(0, settings.update.jobsPlanesMax).map(elm => elm[0]);
      }
      // Sort all icaos per distance
      const list = [];
      for (const icao of icaos) {
        let min = 100000000;
        for (const planeLocation of set) {
          const fr = { latitude: icaodata[planeLocation].lat, longitude: icaodata[planeLocation].lon };
          const to = { latitude: icaodata[icao].lat, longitude: icaodata[icao].lon };
          min = Math.min(min, getDistance(fr, to));
        }
        list.push([icao, min]);
      }
      list.sort((a, b) => a[1] - b[1]);
      set = list.slice(0, 1600*settings.update.jobsPlanesRequests).map(elm => elm[0]);
    }
    i = [...new Set([...i, ...set])];
  }
  let reminder = i.join('-');
  // Split list into chunks smaller than about 8000 chars
  const list = [];
  while (true) {
    let start = reminder.slice(0, 8000);
    reminder = reminder.slice(8000);
    const index = reminder.indexOf('-');
    if (index === -1) {
      list.push(start+reminder);
      break;
    }
    list.push(start+reminder.slice(0, index));
    reminder = reminder.slice(index+1);
  }
  return [i, list];
}


function cleanPlanes(list, username, rentable = true) {
  const planes = {};
  for (const obj of list) {
    // Exclude broken airplanes
    if (obj.NeedsRepair === '1') { continue; }
    // Rented planes discarded, unless rented by current user
    if (obj.RentedBy !== 'Not rented.' && obj.RentedBy !== username) { continue; }
    // If searching for rentable planes, discard planes without
    // dry and web rental price (except planes owned by FSE, because
    // those are reserved for All-In jobs, and planes owned by the user)
    let allin = undefined;
    if (rentable && !parseInt(obj.RentalDry) && !parseInt(obj.RentalWet)) {
      if (obj.Owner === 'Bank of FSE') {
        allin = true; // Plane only used for allin flights
      }
      else if (obj.Owner !== username) { continue; }
    }
    // Planes with fee owned are discarded
    if (parseInt(obj.FeeOwed)) { continue; }
    // Planes in flight are discarded
    if (obj.Location === 'In Flight') { continue; }

    // Ensure location exist in planes object
    if (!planes.hasOwnProperty(obj.MakeModel)) { planes[obj.MakeModel] = {}; }
    if (!planes[obj.MakeModel].hasOwnProperty(obj.Location)) { planes[obj.MakeModel][obj.Location] = []; }

    planes[obj.MakeModel][obj.Location].push({
      id: parseInt(obj.SerialNumber),
      reg: obj.Registration,
      home: obj.Home,
      wet: parseInt(obj.RentalWet),
      dry: parseInt(obj.RentalDry),
      bonus: parseInt(obj.Bonus),
      allin: allin
    });
  }
  return planes;
}

const styles = {
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: '-12px',
    marginLeft: '-12px',
  },
  accSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      mr: 2
    }
  },
  accDetails: {
    flexDirection: 'column'
  },
  title: {
    flexGrow: 1
  },
  chip: {
    fontSize: '0.8em',
    height: 'auto',
    marginBottom: '2px'
  }
};

const filter = createFilterOptions();
const advancedFilter = (options, { inputValue }) => matchSorter(options, inputValue).slice(0, 20);



function AddButton (props) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [key, setKey] = React.useState('');

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setName('');
    setKey('');
    setOpen(false);
  };

  const handleSave = () => {
    handleClose();
    props.onAdd([name, key]);
  }

  return (
    <div>
      <Button onClick={handleClickOpen} sx={{mt: 1}}>
        Add group
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Group information</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Group name"
            fullWidth
            value={name}
            onChange={evt => setName(evt.target.value)}
            margin="normal"
          />
          <TextField
            label="Group read access key"
            fullWidth
            value={key}
            onChange={evt => setKey(evt.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!name || !key}>Add group</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function UpdatePopup(props) {

  const [key, setKey] = React.useState(storage.get('key', ''));
  const [jobsAreas, setJobsAreas] = React.useState(storage.get('jobsAreas', []));
  const [jobsCustom, setJobsCustom] = React.useState(() => {
    const latlngs = storage.get('jobsCustom', []);
    if (latlngs && latlngs.length > 2) { return latlngs; }
    return null;
  });
  const [jobsTime, setJobsTime] = React.useState(storage.get('jobsTime'));
  const [jobsRequests, setJobsRequests] = React.useState(() => getIcaoList(jobsAreas, jobsCustom, [], props.icaodata, props.icaos, props.settings)[1].length);
  const [jobDirection, setJobDirection] = React.useState(props.settings.update.direction);
  const [planeModel, setPlaneModel] = React.useState(storage.get('planeModel', []));
  const [planeUser, setPlaneUser] = React.useState(storage.get('planeUser', []));
  const [planesTime, setPlanesTime] = React.useState(storage.get('planesTime'));
  const [rentablePlanesRequests, setRentablePlanesRequests] = React.useState(planeModel.length);
  const [ownedPlanesRequests, setOwnedPlanesRequests] = React.useState(planeUser.length);
  const [flightTime, setFlightTime] = React.useState(storage.get('flightTime'));
  const [loading, setLoading] = React.useState(false);
  const [openCustom, setOpenCustom] = React.useState(false);
  const [expanded, setExpanded] = React.useState(key ? false : 'panel1');
  const [userList, setUserList] = React.useState([]);
  const [username, setUsername] = React.useState(storage.get('username', ''));
  const [assignmentKeys, setAssignmentKeys] = React.useState(storage.get('assignmentKeys', [{name: 'Personal assignments', enabled: true}]));
  const [layersOptions, setLayersOptions] = React.useState([]);
  const [jobsLayers, setJobsLayers] = React.useState([]);
  const [jobsNb, setJobsNb] = React.useState(0);
  const [flightNb, setFlightNb] = React.useState(0);
  const [planesNb, setPlanesNb] = React.useState(0);
  const { readString } = usePapaParse();
  const { persist } = props.settings.update;


  const areas = React.useState(() => getAreas(props.icaodata, props.icaos))[0];

  const [hits, setHits] = React.useState(0);

  // Update jobs counter when jobs are updated
  React.useEffect(() => {
    setJobsNb(Object.values(props.jobs).reduce((acc,e) => acc + (e["Trip-Only"] ?? []).length + (e["VIP"] ?? []).length + (e["All-In"] ?? []).length, 0));
  }, [props.jobs]);
  // Update flight counter when flight is updated
  React.useEffect(() => {
    setFlightNb(Object.values(props.flight).reduce((acc,e) => acc + (e["Trip-Only"] ?? []).length + (e["VIP"] ?? []).length + (e["All-In"] ?? []).length, 0));
  }, [props.flight]);
  // Update planes counter when planes are updated
  React.useEffect(() => {
    setPlanesNb(Object.values(props.planes).reduce((acc,e) => acc + e.length, 0));
  }, [props.planes]);

  // Update custom area when map center is updated
  React.useEffect(() => {
    const wrapZone = latlngs => {
      if (!latlngs || latlngs.length < 3) { return latlngs; }
      const polygon = L.polygon(latlngs);
      let center = polygon.getBounds().getCenter();
      let diff = 0;
      while (center.lng < props.settings.display.map.center-180) {
        diff += 360;
        center.lng += 360;
      }
      while (center.lng > props.settings.display.map.center+180) {
        diff -= 360;
        center.lng -= 360;
      }
      for (var i = 0; i < latlngs.length; i++) {
        latlngs[i].lng += diff;
      }
      return latlngs;
    }
    // Update stored custom area
    const sJobsCustom = storage.get('jobsCustom', []);
    if (sJobsCustom && sJobsCustom.length >= 3) {
      storage.set('jobsCustom', wrapZone(sJobsCustom));
    }
    // Update current custom area (can be different of stored)
    setJobsCustom(jc => wrapZone(jc));
  }, [props.settings.display.map.center]);

  // Update available custom layers when opening the popup
  React.useEffect(() => {
    if (!props.open) { return; }
    const arr = storage.get('layers', []);
    const l = [];
    for (const layer of arr) {
      if (layer.info && layer.info.type === 'all') {
        l.push({name: layer.info.display.name, id: layer.id, icaos: props.icaos.filter(icao => !hideAirport(icao, layer.info.filters, props.settings.display.sim))});
      }
      if (layer.info && layer.info.type === 'custom') {
        l.push({name: layer.info.display.name, id: layer.id, icaos: layer.info.data.icaos.filter(icao => !hideAirport(icao, layer.info.filters, props.settings.display.sim))});
      }
    }
    const jl = storage.get('jobsLayers', []);
    const layers = l.filter(elm => jl.includes(elm.id));
    setLayersOptions(l);
    setJobsLayers(layers);
    apiHits(false); // remove old entries from counter on init
    setHits((storage.get('apiHits', [])).length);
  }, [props.open, props.icaos, props.icaodata, props.settings.display.sim]);

  // Update the number of request for loading jobs each time one input changes
  React.useEffect(() => {
    let nb = getIcaoList(jobsAreas, jobsCustom, jobsLayers, props.icaodata, props.icaos, props.settings)[1].length;
    if (jobDirection === 'from&to') { nb *= 2; }
    setJobsRequests(nb);
  }, [jobsAreas, jobsCustom, jobsLayers, jobDirection, props.icaodata, props.icaos, props.settings]);

  // Close popup
  const handleClose = () => {
    props.setUpdatePopup(false);
  }

  const setUpdatePopupRef = React.useRef(props.setUpdatePopup);
  React.useEffect(() => {
    // Open popup on loading if no FSE key
    if (!storage.get('key')) {
      setUpdatePopupRef.current(true);
    }

    // Load user and group list
    fetch(process.env.REACT_APP_DYNAMIC_DATA_URL+'users.json').then(response => {
      if (response.ok) {
        response.json().then(arr => {
          setUserList(arr.sort());
        });
      }
    });
  }, []);

  // Loop function to get jobs from FSE
  const updateJobsRequest = (icaosList, jobs, dir, callback) => {
    if (!icaosList.length) {
      callback(jobs);
      return;
    }
    const url = 'data?userkey='+key+'&format=csv&query=icao&search='+dir+'&icaos='+icaosList.pop();
    // Fetch job list
    fetch(process.env.REACT_APP_PROXY+url)
    .then(function(response) {
      if (!response.ok) {
        throw new NetworkError(response.status);
      }
      return response.text();
    })
    .then(function(csv) {
      // Parse CSV
      const parse = readString(csv, {header: true, skipEmptyLines: 'greedy'});
      if (parse.errors.length > 0) {
        throw new ParsingError(csv);
      }
      updateJobsRequest(icaosList, [...jobs, ...parse.data], dir, callback);
    })
    .catch(function(error) {
      log.error("Error while updating Jobs", error);
      updateAlert(error);
      setLoading(false);
    })
    .finally(() => {
      apiHits();
    });
  }
  // Save jobs
  const saveJobs = (jobs) => {
    // Update jobs
    storage.set('jobs', jobs, true);
    props.setJobs(jobs);
    // Update date
    let date = new Date().toString();
    storage.set('jobsTime', date);
    setJobsTime(date);
    // Update area
    storage.set('jobsAreas', jobsAreas);
    storage.set('jobsCustom', jobsCustom);
    storage.set('jobsLayers', jobsLayers.map(elm => elm.id));
    // Close popup
    setLoading(false);
    if ( ! persist ) {
      handleClose();
    } else {
      setExpanded(false);
    }
  }
  // Jobs Update button clicked
  const updateJobs = (evt) => {
    evt.stopPropagation();
    setLoading('panel2');
    // Compute ICAO list
    const [icaos, icaosList] = getIcaoList(jobsAreas, jobsCustom, jobsLayers, props.icaodata, props.icaos, props.settings);
    if (icaos.length === 0) {
      alert('No airport in selected job area');
      setLoading(false);
      return false;
    }
    const dir = jobDirection === 'to' ? 'jobsto' : 'jobsfrom';
    updateJobsRequest([...icaosList], [], dir, (list) => {
      const jobs = cleanJobs(list, props.icaodata, props.settings.update, jobDirection === 'both' ? icaos : null);
      if (jobDirection === 'from&to') {
        updateJobsRequest(icaosList, [], 'jobsto', (list) => {
          const jobs2 = cleanJobs(list, props.icaodata, props.settings.update, null);
          for (const key of Object.keys(jobs2)) {
            if (!jobs.hasOwnProperty(key)) {
              jobs[key] = jobs2[key];
            }
          }
          saveJobs(jobs);
        });
      }
      else {
        saveJobs(jobs);
      }
    });
  }
  // Jobs Clicked button clicked
  const clearJobs = (evt) => {
    evt.stopPropagation();
    setLoading('panel2');
    // Update planes
    props.setJobs({});
    storage.remove('jobs');
    storage.remove('jobsTime');
    setJobsTime(null);
    // Close popup
    setLoading(false);
    if ( ! persist ) {
      handleClose();
    }
  }

  // Loop function to get planes from FSE
  const updateRentablePlanesRequest = (models, planes, callback) => {
    if (!models.length) {
      callback(planes);
      return;
    }
    const url = 'data?userkey='+key+'&format=csv&query=aircraft&search=makemodel&makemodel='+encodeURIComponent(models.pop());
    // Fetch plane list
    fetch(process.env.REACT_APP_PROXY+url)
    .then(function(response) {
      if (!response.ok) {
        throw new NetworkError(response.status);
      }
      return response.text();
    })
    .then(function(csv) {
      // Parse CSV
      const parse = readString(csv, {header: true, skipEmptyLines: 'greedy'});
      if (parse.errors.length > 0) {
        throw new ParsingError(csv);
      }
      // Convert array to object
      updateRentablePlanesRequest(models, [...planes, ...parse.data], callback);
    })
    .catch(function(error) {
      log.error("Error while updating Rentable Planes", error);
      updateAlert(error);
      setLoading(false);
    })
    .finally(() => {
      apiHits();
    });
  }
  const updateOwnedPlanesRequest = (usernames, planes, callback) => {
    if (!usernames.length) {
      callback(planes);
      return;
    }
    const url = 'data?userkey='+key+'&format=csv&query=aircraft&search=ownername&ownername='+encodeURIComponent(usernames.pop());
    // Fetch plane list
    fetch(process.env.REACT_APP_PROXY+url)
    .then(function(response) {
      if (!response.ok) {
        throw new NetworkError(response.status);
      }
      return response.text();
    })
    .then(function(csv) {
      // Parse CSV
      const parse = readString(csv, {header: true, skipEmptyLines: 'greedy'});
      if (parse.errors.length > 0) {
        throw new ParsingError(csv);
      }
      // Convert array to object
      updateOwnedPlanesRequest(usernames, [...planes, ...parse.data], callback);
    })
    .catch(function(error) {
      log.error("Error while updating User Planes", error);
      updateAlert(error);
      setLoading(false);
    })
    .finally(() => {
      apiHits();
    });
  }
  // Planes Update button clicked
  const updatePlanes = (evt) => {
    evt.stopPropagation();
    setLoading('panel3');
    updateRentablePlanesRequest(planeModel.slice(), [], (list1) => {
      updateOwnedPlanesRequest(planeUser.slice(), [], (list2) => {
        const planes = cleanPlanes(list1, username, true);
        const p2 = cleanPlanes(list2, username, false);
        for (const model of Object.keys(p2)) {
          if (!planes[model]) { planes[model] = {}; }
          for (const icao of Object.keys(p2[model])) {
            if (!planes[model][icao]) { planes[model][icao] = []; }
            const ids = planes[model][icao].map(elm => elm.id);
            p2[model][icao].forEach(elm => {
              if (!ids.includes(elm.id)) { planes[model][icao].push(elm); }
            });
          }
        }
        storage.set('planes', planes);
        props.setPlanes(planes);
        let date = new Date().toString();
        storage.set('planesTime', date);
        setPlanesTime(date);
        storage.set('planeModel', planeModel);
        storage.set('planeUser', planeUser);
        setLoading(false);
        if ( ! persist ) {
          handleClose();
        } else {
          setExpanded(false);
        }
      });
    });
  }
  // Planes Clear button clicked
  const clearPlanes = (evt) => {
    evt.stopPropagation();
    setLoading('panel3');
    // Update planes
    props.setPlanes({});
    storage.remove('planes');
    storage.remove('planesTime');
    setPlanesTime(null);
    // Close popup
    setLoading(false);
    if ( ! persist ) {
      handleClose();
    }
  }

  // Loop function to get assignments from FSE
  const updateFlightRequest = (groups, results, callback) => {
    if (!groups.length) {
      callback(results);
      return;
    }
    const group = groups.pop();
    // Check if the group is enabled
    if (!group.enabled) {
      updateFlightRequest(groups, results, callback);
      return;
    }
    // If no group key, it is personal assignments
    const groupKey = group.key ? group.key : key;
    // Build URL
    const url = 'data?userkey='+key+'&format=csv&query=assignments&search=key&readaccesskey='+groupKey
    // Fetch job list
    fetch(process.env.REACT_APP_PROXY+url)
    .then(function(response) {
      if (!response.ok) {
        throw new NetworkError(response.status);
      }
      return response.text();
    })
    .then(function(csv) {
      // Parse CSV
      const parse = readString(csv, {header: true, skipEmptyLines: 'greedy'});
      if (parse.errors.length > 0) {
        throw new ParsingError(csv);
      }
      updateFlightRequest(groups, [...results, ...parse.data], callback);
    })
    .catch(function(error) {
      log.error("Error while updating assignments", error);
      updateAlert(error);
      setLoading(false);
    })
    .finally(() => {
      apiHits();
    });
  }

  // My assignments Update button clicked
  const updateFlight = (evt) => {
    evt.stopPropagation();
    setLoading('panel4');
    updateFlightRequest([...assignmentKeys], [], arr => {
      const jobs = cleanJobs(arr, props.icaodata, props.settings.update);
      // Update flight
      storage.set('flight', jobs);
      props.setFlight(jobs);
      // Update date
      let date = new Date().toString();
      storage.set('flightTime', date);
      setFlightTime(date);
      // Close popup
      setLoading(false);
      if ( ! persist ) {
        handleClose();
      } else {
        setExpanded(false);
      }
    });
  }
  // My assignments Clear button clicked
  const clearFlight = (evt) => {
    evt.stopPropagation();
    setLoading('panel4');
    // Update planes
    props.setFlight({});
    storage.remove('flight');
    storage.remove('flightTime');
    setFlightTime(null);
    // Close popup
    setLoading(false);
    if ( ! persist ) {
      handleClose();
    }
  }

  const panelChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const labelAroundPlanes = () => {
    if (props.settings.update.jobsPlanes === 'strict') {
      return 'Airports with an available plane';
    }
    return 'Top '+props.settings.update.jobsPlanesMax+' areas with an available plane';
  }

  return (
    <Dialog onClose={handleClose} open={props.open} fullWidth={true} maxWidth="sm">
      <DialogTitle>
        Load data from FSE
        <IconButton
          onClick={handleClose}
          size="large"
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey[500]',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>

        <Alert severity="warning" sx={{ mb: 2 }}>You are limited to 40 requests every 6 hours (~1 request every 10 minutes).</Alert>

        {!!hits && (
          <Alert severity={hits > 34 ? 'error' : hits > 24 ? 'warning' : 'info'} sx={{ mb: 2 }}><span style={{fontWeight: 600}}>{hits}</span> request{hits !== 1 ? 's' : ''} in the past 6 hours</Alert>
        )}

        <Box>
          <Accordion expanded={expanded === 'panel1'} onChange={panelChange('panel1')} data-tour="Step4">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>FSE information</Typography>
            </AccordionSummary>
            <AccordionDetails sx={styles.accDetails}>
              <Grid container spacing={3}>
                <Grid size={6}>
                  <TextField
                    label="Read Access Key"
                    type="text"
                    onChange={(evt) => {
                      let k = evt.target.value;
                      setKey(k);
                      storage.set('key', k);
                    }}
                    value={key}
                    helperText={<span>Can be found <Link href="https://server.fseconomy.net/datafeeds.jsp" target="_blank">here</Link></span>}
                    variant='outlined'
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={6}>
                  <TextField
                    label="Username"
                    type="text"
                    onChange={(evt) => {
                      let u = evt.target.value;
                      setUsername(u);
                      storage.set('username', u);
                    }}
                    value={username}
                    variant='outlined'
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Typography variant="body2" sx={{ mt: 1 }}>The Read Access Key is stored on your own computer, and is only used for datafeed updates at your request.</Typography>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel2'} onChange={panelChange('panel2')} data-tour="Step5">
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accSummary}>
              <Typography sx={styles.title}>Jobs {jobsNb > 0 && <Chip label={jobsNb} color="primary" size="small" component="span" sx={styles.chip} />}</Typography>
              <Button color="secondary" onClick={clearJobs} component="span">
                Clear
              </Button>
              &nbsp;
              <Tooltip title={<span>Last update : {jobsTime ? ((new Date(jobsTime)).toLocaleString()) : "never"}</span>}>
                <span>
                  <Button variant="contained" color="primary" onClick={updateJobs} disabled={loading !== false || !key || (!jobsAreas.length && !jobsLayers.length) || jobsRequests > 10} component="span">
                    Update
                    {loading === 'panel2' && <CircularProgress size={24} sx={styles.buttonProgress} />}
                  </Button>
                </span>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails sx={styles.accDetails}>
              <Autocomplete
                multiple
                limitTags={2}
                options={areas}
                renderInput={(params) => (
                  <TextField {...params} label='Load from geographical area' variant='outlined' margin="normal" />
                )}
                onChange={(evt, value) => {
                  if (value.includes('Custom area') && !jobsAreas.includes('Custom area')) {
                    setOpenCustom(true);
                  }
                  else {
                    setJobsAreas(value);
                  }
                }}
                value={jobsAreas}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  if (!filtered.includes('Custom area')) {
                    filtered.unshift('Custom area');
                  }
                  return filtered;
                }}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    { option === 'Custom area' ?
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          fontWeight: 'bold'
                        }}
                      >
                        <AspectRatioIcon />&nbsp;Select custom area on map
                      </Box>
                    : option === 'Around planes' ?
                      <Box
                        component="span"
                        sx={{
                          display: 'flex',
                          fontWeight: 'bold'
                        }}
                      >
                        <AirplanemodeActiveIcon />&nbsp;{labelAroundPlanes()}
                      </Box>
                    :
                      option
                    }
                  </li>
                )}
                getOptionLabel={option => {
                  if (option === 'Around planes') {
                    return labelAroundPlanes();
                  }
                  return option;
                }}
              />
              <CustomAreaPopup
                open={openCustom}
                handleClose={() => setOpenCustom(false)}
                setArea={(latlngs) => {
                  const a = [...jobsAreas, 'Custom area'];
                  setJobsCustom(latlngs);
                  setJobsAreas(a);
                }}
                latlngs={jobsCustom}
                settings={props.settings}
              />
              <Autocomplete
                multiple
                options={layersOptions}
                renderInput={(params) => (
                  <TextField {...params} label='Load from custom layers' variant='outlined' margin="normal" />
                )}
                onChange={(evt, values) => {
                  setJobsLayers(values);
                }}
                value={jobsLayers}
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                getOptionLabel={option => option.name}
                noOptionsText="No custom layer"
                margin="normal"
              />
              { jobsRequests >= 2 && jobsRequests < 10 && <Alert severity="warning" sx={{ mt: 1 }}>Selected area is very large, it will require {jobsRequests} requests (10 max).</Alert> }
              { jobsRequests >= 10 && <Alert severity="error" sx={{ mt: 1 }}>Selected area is too big.</Alert> }
              <TextField
                label="Job direction for the selected area/layer"
                variant="outlined"
                value={jobDirection}
                onChange={(evt) => setJobDirection(evt.target.value)}
                select
                fullWidth
                margin="normal"
              >
                <MenuItem value="from">Jobs FROM the selected area/layer</MenuItem>
                <MenuItem value="to">Jobs TO the selected area/layer</MenuItem>
                <MenuItem value="from&to">Jobs FROM and TO the selected area/layer</MenuItem>
                <MenuItem value="both">Jobs INSIDE the selected area/layer</MenuItem>
              </TextField>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel3'} onChange={panelChange('panel3')} data-tour="Step6">
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accSummary}>
              <Typography sx={styles.title}>Available planes {planesNb > 0 && <Chip label={planesNb} color="primary" size="small" component="span" sx={styles.chip} />}</Typography>
              <Button color="secondary" onClick={clearPlanes} component="span">
                Clear
              </Button>
              &nbsp;
              <Tooltip title={<span>Last update : {planesTime ? ((new Date(planesTime)).toLocaleString()) : "never"}</span>}>
                <span>
                  <Button variant="contained" color="primary" onClick={updatePlanes} disabled={loading !== false || !key || (!planeModel.length && !planeUser.length) || rentablePlanesRequests + ownedPlanesRequests > 10} component="span">
                    Update
                    {loading === 'panel3' && <CircularProgress size={24} sx={styles.buttonProgress} />}
                  </Button>
                </span>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails sx={styles.accDetails}>
              <Autocomplete
                multiple
                limitTags={2}
                options={Object.keys(aircrafts)}
                renderInput={(params) => (
                  rentablePlanesRequests > 1 ?
                    <TextField {...params} label='Rentable planes: aircraft models' variant='outlined' error helperText={rentablePlanesRequests+' models selected, it will require '+rentablePlanesRequests+' requests (10 max)'} />
                  :
                    <TextField {...params} label='Rentable planes: aircraft model' variant='outlined' />
                )}
                onChange={(evt, value) => {
                  setPlaneModel(value);
                  setRentablePlanesRequests(value.length);
                }}
                value={planeModel}
              />
              <Autocomplete
                multiple
                limitTags={2}
                options={userList}
                renderInput={(params) => (
                  ownedPlanesRequests > 1 ?
                    <TextField {...params} label='Owned & leased planes: user or group names' variant='outlined' error helperText={ownedPlanesRequests+' users/groups selected, it will require '+ownedPlanesRequests+' requests (10 max)'} />
                  :
                    <TextField {...params} label='Owned & leased planes: user or group name' variant='outlined' />
                )}
                onChange={(evt, value) => {
                  setPlaneUser(value);
                  setOwnedPlanesRequests(value.length);
                }}
                filterOptions={(options, params) => advancedFilter(options, params)}
                getOptionLabel={option => he.decode(option)}
                value={planeUser}
                sx={{ mt: 2 }}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel4'} onChange={panelChange('panel4')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accSummary}>
              <Typography sx={styles.title}>My assignments {flightNb > 0 && <Chip label={flightNb} color="primary" size="small" component="span" sx={styles.chip} />}</Typography>
              <Button color="secondary" onClick={clearFlight} component="span">
                Clear
              </Button>
              &nbsp;
              <Tooltip title={<span>Last update : {flightTime ? ((new Date(flightTime)).toLocaleString()) : "never"}</span>}>
                <span>
                  <Button variant="contained" color="primary" onClick={updateFlight} disabled={loading !== false || !key} component="span">
                    Update
                    {loading === 'panel4' && <CircularProgress size={24} sx={styles.buttonProgress} />}
                  </Button>
                </span>
              </Tooltip>
            </AccordionSummary>
            <AccordionDetails>
              <Stack direction="row" spacing={1}>
                { assignmentKeys.map((elm, id) => (
                  <Chip
                    label={elm.name}
                    onClick={() => {
                      const arr = [...assignmentKeys];
                      arr[id].enabled = !arr[id].enabled;
                      setAssignmentKeys(arr);
                      storage.set('assignmentKeys', arr);
                    }}
                    onDelete={id === 0 ? null : (() => {
                      const arr = [...assignmentKeys];
                      arr.splice(id, 1);
                      setAssignmentKeys(arr);
                      storage.set('assignmentKeys', arr);
                    })}
                    icon={elm.enabled ? <RadioButtonCheckedIcon /> : <RadioButtonUncheckedIcon />}
                    key={elm.name+elm.id}
                  />
                )) }
              </Stack>
              <AddButton
                onAdd={([name, key]) => {
                  const arr = [...assignmentKeys];
                  arr.push({name: name, key: key, enabled: true})
                  setAssignmentKeys(arr);
                  storage.set('assignmentKeys', arr);
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

      </DialogContent>
    </Dialog>
  );
}

export default UpdatePopup;
